/* eslint-disable complexity */
import SectionMessage from '@atlaskit/section-message';
import {
  LinkClickedEvent,
  LozengeClickedEvent,
  MarketplaceAnalyticsContext,
  MenuExpandedEvent,
  MenuItemClickedEvent,
  TabClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import { AppListing, ELEMENT_ID, MediaViewerResource } from '@atlassian/mpac-app-listing';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BodyNormal, LinkWithIcon } from '@atlassian/mpac-primitives';
import { type SelectedSiteAndProduct } from '@atlassian/mpac-site-selection';
import Telemetry from '@atlassian/mpac-telemetry';
import {
  AppDeployment,
  AppListingTab,
  HostingTypesWithCloudComplianceBoundaries,
  HostingTypeToQueryParam,
  RUNS_ON_ATLASSIAN_PROGRAM,
} from '@atlassian/mpac-types';
import {
  type MarketplaceStoreEditionType,
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/graphql-gateway-types';
import { PartnerPermissions } from '@atlassian/mpac-ui';
import {
  appSupportsEditions,
  convertToAtlassianHostingType,
  DOMUtils,
  MarketplaceAppUtils,
  UrlUtils,
  WindowUtils,
} from '@atlassian/mpac-utils';
import capitalize from 'lodash/capitalize';
import React, { useLayoutEffect } from 'react';

import {
  safeToForwardToPartner,
  trackDownloadLink,
  trackInstallClick,
  trackLightboxView,
  trackLookingForDataCenter,
  trackRequestClick,
  trackSeeMoreReviews,
  trackVendorSupportLinkClick,
  trackWatchAction,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  useAppListingPageAnalytics,
  useGoogleAnalyticsExtraContext,
  useVendorGoogleAnalytics,
} from '../../../../../../../../hooks';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_NAV_BAR,
  ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
  ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
  ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
  ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
  ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
  ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
} from '../../../../../../../analytics/Events';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import { TEST_IDS } from '../../../../../../../common/testIds';
import {
  AGC_ROA_INFO_MESSAGE,
  AGC_SECTION_MESSAGE,
  AGC_SECTION_SUB_MESSAGE,
  IC_SECTION_MESSAGE,
  STARGATE_BASE_PATH,
} from '../../../../../../../constants';
import {
  DATA_CENTER_EOS_MESSAGE_DESCRIPTION,
  DATA_CENTER_EOS_MESSAGE_TITLE,
  DATA_CENTER_MIGRATION_LINK,
  EXPLORE_CLOUD_MIGRATION_LINK,
  SERVER_EOL_MESSAGE_FOR_DATA_CENTER_SUPPORTED_APPS,
  SERVER_EOL_MESSAGE_FOR_NON_DATA_CENTER_SUPPORTED_APPS,
  SERVER_EOS_MESSAGE,
} from '../../../../../../../constants/ServerEoS';
import useCloudMigrationInfo from '../../../../../../../hooks/useCloudMigration';
import useLoggedInUser from '../../../../../../../hooks/useLoggedInUser';
import AdminHeaderBanners from '../AdminHeaderBanners/AdminHeaderBanners';
import AdminHeaderLinks from '../AdminHeaderLinks';
import AppListingJSDWidget from '../AppListingJSDWidget';
import AppListingMeta from '../AppListingMeta/AppListingMeta';
import {
  getEmceeAppListingInstallLink,
  getEmceeAppListingRequestAppLink,
  getInstalledAppProductLink,
  onlyCompatibleProducts,
} from '../AppsRoute.utils';
import HeaderInjection from '../HeaderInjection';
import SiteSelectorDialogWrapper from '../HeaderInjection/SiteSelectorDialogWrapper';
import { SITE_SELECTOR_VISIBLE_QUERY_PARAM, useCTAClickHandler } from '../hooks/useCTAClickHandler';
import usePartnerPermissions from '../hooks/usePartnerPermissions';
import {
  getMigrationAssistanceStatus,
  getMigrationInformationAvailable,
  hasNewerPrivateVersion,
  toAnalyticsHosting,
} from '../utils';
import { InfoContainerAfterHeader, WarningContainerAfterHeader } from './styles';
import { serverEOLAppsWithDcVersionAvailable } from './utils';

import type {
  AppDetailsFromSearchResponse,
  AppEditionDetails,
  AppReviewSummaryDetails,
  AppVersion,
  HostingOptionKindType,
  MarketplaceAppByKeyQuery,
  MediaViewerResourceType,
  SupportResourcesLinkType,
} from '@atlassian/mpac-app-listing';
import type {
  AppListingCloudMigration,
  AppListingTabsType,
  AppListingUser,
  BootstrapLinks,
  HostingWithCloudComplianceBoundariesType,
} from '@atlassian/mpac-types';
import type { SyntheticEvent } from 'react';

type BeforeHeaderContentProps = {
  appId: string;
  links: BootstrapLinks;
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  showOnlyPublic: boolean;
};

const BeforeHeaderContent = ({
  links,
  appId,
  appDetails,
  showOnlyPublic,
}: BeforeHeaderContentProps) => {
  const hasNewerPvtVersionForAnyHosting =
    hasNewerPrivateVersion(appDetails.privateCloudVersion, appDetails.publicCloudVersion) ||
    hasNewerPrivateVersion(appDetails.privateServerVersion, appDetails.publicServerVersion) ||
    hasNewerPrivateVersion(appDetails.privateDataCenterVersion, appDetails.publicDataCenterVersion);

  return (
    <>
      <AdminHeaderBanners
        appManageLink={links.appManageLink.expand({ appId })}
        hasNewerPrivateVersion={hasNewerPvtVersionForAnyHosting}
        listingStatus={appDetails.listingStatus}
        showOnlyPublic={showOnlyPublic}
      />
      <AdminHeaderLinks
        appManageLink={links.appManageLink.expand({ appId })}
        appShortLink={links.appShortLink.expand({ appId })}
      />
    </>
  );
};

type AppListingPageContentProps = {
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  editions?: Array<AppEditionDetails>;
  reviewSummaryDetails?: AppReviewSummaryDetails;
  appDetailsFromSearchResponse: AppDetailsFromSearchResponse;
  appId: string;
  setNewTab: (tab: AppListingTabsType) => void;
  setNewHostingType: (hostingType: HostingWithCloudComplianceBoundariesType) => void;
  selectedHosting: HostingWithCloudComplianceBoundariesType;
  selectedAppVersion: AppVersion;
  tab: AppListingTabsType;
  links: BootstrapLinks;
  showOnlyPublic: boolean;
  pageRouteSource?: string | null;
};
// eslint-disable-next-line max-lines-per-function
const AppListingPageContent = (
  props: AppListingPageContentProps & {
    cloudMigrationInfo: AppListingCloudMigration | null | undefined;
  }
) => {
  const { isLoggedIn, isAdmin, userAvatarUrl, user } = useLoggedInUser();
  const { enableMediaFromProductListing, enableSelfServeAppEditions, showDCEosMessaging } =
    useStatsig();
  const {
    appDetails,
    editions,
    reviewSummaryDetails,
    appDetailsFromSearchResponse,
    appId,
    selectedHosting,
    selectedAppVersion,
    tab,
    showOnlyPublic,
    links,
    cloudMigrationInfo,
    pageRouteSource,
  } = props;
  const {
    mpacBaseUrl,
    jiraServiceDeskEmbedBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
    graphQLGatewayUrl,
  } = BootstrapConfig.get();
  const loginHref = `/login?redirect=${encodeURIComponent(window.location.href)}`;

  const appPricingDetailsLink = links.appPricingDetails.expand({ appKey: appDetails.appKey });

  const isEditionsSupported = appSupportsEditions({
    editions,
    hostingType: selectedHosting,
  });
  const showAppPlans = enableSelfServeAppEditions() && isEditionsSupported;

  const fireAnalyticsEvent = useAnalyticsEvents();
  const { permissions } = usePartnerPermissions(mpacBaseUrl, appDetails.partner?.id, isLoggedIn);
  const isSelectedHostingIC =
    selectedHosting === HostingTypesWithCloudComplianceBoundaries.IsolatedCloud;
  const isSelectedHostingAGC =
    selectedHosting === HostingTypesWithCloudComplianceBoundaries.GovernmentCloud;
  const serverAppVersion = appDetails.serverAppVersions.edges?.[0]?.node;
  const dataCenterAppVersion = appDetails.dataCenterAppVersions.edges?.[0]?.node;
  const cloudAppVersion = appDetails.cloudAppVersions.edges?.[0]?.node;
  const agcAppVersion = appDetails.agcAppVersions?.edges?.[0]?.node;
  const isolatedCloudAppVersion = appDetails.isolatedCloudAppVersions?.edges?.[0]?.node;
  const versionInfo = {
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
    agcAppVersion,
    isolatedCloudAppVersion,
  };

  useAppListingPageAnalytics({
    hostingType: convertToAtlassianHostingType(selectedHosting),
    pricingModel: selectedAppVersion.paymentModel,
    appDetails,
    pageRouteSource,
  });

  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: appDetails.googleAnalyticsId,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  // Track using Google analytics 4 additionally if feature flag is on and ga4 id is present.
  // Once ga4 migration is complete, the flag and ga3 tracking will be removed.
  // https://ecosystem.atlassian.net/browse/AMKT-29181
  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: appDetails.googleAnalytics4Id,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  useGoogleAnalyticsExtraContext({
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  });

  useLayoutEffect(() => {
    Telemetry.timeToFirstMeaningfulPaint(performance.now());
  }, []);

  const instrumentHostingClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    fireAnalyticsEvent({
      eventType: MenuItemClickedEvent,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        label: toAnalyticsHosting(hosting),
        hosting: toAnalyticsHosting(hosting),
        selectionType: hostingOptionKind,
      },
    });
  };

  const handleTabChange = (newTab: AppListingTabsType) => {
    props.setNewTab(newTab);

    fireAnalyticsEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        label: capitalize(newTab),
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        appListingTabKey: capitalize(newTab),
      },
    });
  };

  const handleHostingChange = (hosting: HostingWithCloudComplianceBoundariesType) => {
    props.setNewHostingType(hosting);
  };

  const handleVendorContactClick = (hostingType: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        source: hostingType,
      },
    });
  };

  const handleCloudMigrationResourcesLinksClick = (label: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
      source: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
        label,
      },
    });
  };

  const handleSupportLinkClick = (supportLink: SupportResourcesLinkType) => {
    safeToForwardToPartner(appId, () => {
      trackVendorSupportLinkClick(supportLink);
    });
  };

  const handleMediaViewerOpen = (resourceType: MediaViewerResourceType, imageUrl: string) => {
    safeToForwardToPartner(appId, () => {
      trackLightboxView(imageUrl, resourceType === MediaViewerResource.Highlight, mpacCdnBaseUrl);
    });
  };

  const handleWatchAppToggle = (toggleOn: boolean) => {
    safeToForwardToPartner(appId, () => {
      trackWatchAction(toggleOn ? 'Watch' : 'Unwatch');
    });
  };

  const handleSeeMoreReviewsClick = () => {
    safeToForwardToPartner(appId, () => {
      trackSeeMoreReviews();
    });
  };

  const handleArtifactDownloadClick = (url: string) => {
    const { pathname } = new URL(url);
    safeToForwardToPartner(appId, () => {
      trackDownloadLink(pathname);
    });
  };

  const handleHostingSelectOpen = () => {
    fireAnalyticsEvent({
      eventType: MenuExpandedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        hostingOptions: appDetails.productHostingOptions.map(toAnalyticsHosting),
      },
    });
  };

  const handleHostingOptionClick = (
    hosting: HostingWithCloudComplianceBoundariesType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    instrumentHostingClick(convertToAtlassianHostingType(hosting), hostingOptionKind);

    if (hosting === AtlassianProductHostingType.DataCenter) {
      safeToForwardToPartner(appId, () => {
        trackLookingForDataCenter();
      });
    }
  };

  const handleSiteSelectorInstallCTAClick = (
    canUserInstallApp: boolean,
    event?: SyntheticEvent<HTMLLinkElement>,
    installCTAHref?: string
  ) => {
    event?.preventDefault();
    safeToForwardToPartner(appId, () => {
      if (canUserInstallApp) {
        trackInstallClick(capitalize(AtlassianProductHostingType.Cloud));
      } else {
        trackRequestClick(capitalize(AtlassianProductHostingType.Cloud));
      }
    });

    if (installCTAHref) {
      WindowUtils.redirectTo(installCTAHref);
    }
  };

  const handleMigrationLozengeClick = () => {
    handleTabChange(AppListingTab.Overview);
    setTimeout(() => {
      DOMUtils.scrollToAndFocusElement(`#${ELEMENT_ID.CLOUD_MIGRATION_SECTION_LINKS}`);
    }, 0);
    fireAnalyticsEvent({
      eventType: LozengeClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      },
    });
  };

  const {
    siteSelectorOpen,
    selectedPlan,
    onSiteSelectorClose,
    activeCTAKind,
    hostingSelectorPurchaseIntent,
    onRequestAppClick,
    onAppListingHeaderCTAClick,
    onHostingSelectorCTAClick,
    onPricingCalculatorCTAClick,
    showOneClickAppInstallDialog,
    showTwoClickAppInstallFocusedTask,
    availableSiteCloudIds,
    selectedPlanForPlanSelector,
    setSelectedPlanForPlanSelector,
  } = useCTAClickHandler({
    appKey: appDetails.appKey,
    appId,
    userLoggedIn: isLoggedIn,
    userEmail: user?.email,
    selectedHosting: convertToAtlassianHostingType(selectedHosting),
    paymentModel: selectedAppVersion.paymentModel,
    compatibleProducts: selectedAppVersion.deployment.compatibleProducts,
    cloudAppVersion,
    artifactDownloadLink: links.artifactDownload,
    editionTypeList: editions?.map(({ type }) => type) || [],
    pageRouteSource,
  });
  const getHeaderInjection = () => {
    const hasPublicCloudVersion = !!appDetails.publicCloudVersion.edges?.length;
    const hasPrivateCloudVersion = !!appDetails.privateCloudVersion.edges?.length;

    // An app is private remoteable only, if it has no public version
    // and at-least one private version
    const isPrivateRemoteableOnly = !hasPublicCloudVersion && hasPrivateCloudVersion;
    return (
      <HeaderInjection
        appKey={appDetails.appKey}
        appId={appId}
        appName={appDetails.name}
        selectedHosting={selectedHosting}
        privateListingsLink={links.managePrivateListing}
        privateRemotableOnly={isPrivateRemoteableOnly}
        appDetailsLink={links.appDetails}
        artifactDownloadLink={links.artifactDownload}
        trialLicenseActivationLink={links.trialLicenseActivation}
        licensePurchaseLink={links.licensePurchase}
        versionInfo={versionInfo}
        productHostingOptions={appDetails.productHostingOptions}
        activeCTAKind={activeCTAKind}
        hostingSelectorPurchaseIntent={hostingSelectorPurchaseIntent}
        onRequestAppClick={onRequestAppClick}
        onAppListingHeaderCTAClick={onAppListingHeaderCTAClick}
        onHostingSelectorCTAClick={onHostingSelectorCTAClick}
        watchersInfo={appDetails.watchersInfo}
        isLoggedIn={isLoggedIn}
        loginHref={loginHref}
      />
    );
  };

  const renderBeforeHeader = () => {
    return (
      <BeforeHeaderContent
        appId={appId}
        appDetails={appDetails}
        links={links}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  const contentAfterHeader = () => {
    if (isSelectedHostingIC) return IC_SECTION_MESSAGE;
    else if (isSelectedHostingAGC)
      return (
        <>
          <b>{AGC_SECTION_MESSAGE}</b>
          <br />
          {AGC_SECTION_SUB_MESSAGE}
        </>
      );
    else if (isDataCenterAppEos)
      return (
        <>
          <strong>{DATA_CENTER_EOS_MESSAGE_TITLE}</strong> {DATA_CENTER_EOS_MESSAGE_DESCRIPTION}
        </>
      );
    else if (isServerAppEos) return SERVER_EOS_MESSAGE;
    else if (showServerEolWarning) {
      return inAllowListDataCenterSupportedApps
        ? SERVER_EOL_MESSAGE_FOR_DATA_CENTER_SUPPORTED_APPS
        : SERVER_EOL_MESSAGE_FOR_NON_DATA_CENTER_SUPPORTED_APPS;
    } else return '';
  };

  const inAllowListDataCenterSupportedApps = Boolean(
    serverEOLAppsWithDcVersionAvailable?.includes(appDetails.appKey)
  );

  const renderAfterHeader = () => {
    const SectionMessageBody = BodyNormal;
    if (isSelectedHostingAGC || isSelectedHostingIC) {
      const isRunsOnAtlassianApp =
        appDetails?.tags?.marketingTags?.some((tag) => tag.name === RUNS_ON_ATLASSIAN_PROGRAM) ||
        false;
      return (
        <>
          {isRunsOnAtlassianApp && (
            <InfoContainerAfterHeader
              data-testid={
                isSelectedHostingIC
                  ? TEST_IDS.IC_INFO_ROA_WARNING_MESSAGE
                  : TEST_IDS.AGC_INFO_ROA_WARNING_MESSAGE
              }
            >
              <SectionMessage appearance="warning">
                <SectionMessageBody>{AGC_ROA_INFO_MESSAGE}</SectionMessageBody>
              </SectionMessage>
            </InfoContainerAfterHeader>
          )}
          <InfoContainerAfterHeader
            data-testid={isSelectedHostingIC ? TEST_IDS.IC_INFO_MESSAGE : TEST_IDS.AGC_INFO_MESSAGE}
          >
            <SectionMessage appearance="information">
              <SectionMessageBody>{contentAfterHeader()}</SectionMessageBody>
            </SectionMessage>
          </InfoContainerAfterHeader>
        </>
      );
    }
    const actionLink = (
      <LinkWithIcon
        size="medium"
        type="external"
        href={isDataCenterAppEos ? DATA_CENTER_MIGRATION_LINK : EXPLORE_CLOUD_MIGRATION_LINK}
        aria-label={isDataCenterAppEos ? 'Learn more and get support' : 'Explore migration program'}
      >
        {isDataCenterAppEos ? 'Learn more and get support' : 'Explore migration program'}
      </LinkWithIcon>
    );

    if (isServerAppEos || showServerEolWarning || isDataCenterAppEos)
      return (
        <WarningContainerAfterHeader
          data-testid={
            isDataCenterAppEos
              ? TEST_IDS.DATA_CENTER_EOS_MESSAGE
              : isServerAppEos
              ? TEST_IDS.SERVER_EOS_MESSAGE
              : TEST_IDS.SERVER_EOL_MESSAGE
          }
        >
          <SectionMessage appearance="information" actions={[actionLink]}>
            <SectionMessageBody>{contentAfterHeader()}</SectionMessageBody>
          </SectionMessage>
        </WarningContainerAfterHeader>
      );
  };

  const metaTagImage =
    selectedAppVersion.heroImage ||
    (selectedAppVersion.highlights?.length && selectedAppVersion.highlights[0].croppedScreenshot) ||
    appDetails.logo;

  const cloudAppCompatibilities = cloudAppVersion
    ? onlyCompatibleProducts(selectedAppVersion.deployment.compatibleProducts).map(
        (product) => product.id
      )
    : [];

  const installAppHrefResolver = (
    selectedSiteProduct: SelectedSiteAndProduct,
    directInstall = false,
    selectedAppPlan?: MarketplaceStoreEditionType,
    selectedUserCount?: number
  ) =>
    getEmceeAppListingInstallLink(
      appDetails.appKey,
      selectedSiteProduct,
      user?.email,
      showOneClickAppInstallDialog || showTwoClickAppInstallFocusedTask ? !directInstall : true,
      directInstall,
      selectedAppPlan,
      selectedUserCount
    );

  const requestAppHrefResolver = (
    selectedSiteProduct: SelectedSiteAndProduct,
    selectedAppPlan?: MarketplaceStoreEditionType
  ) =>
    getEmceeAppListingRequestAppLink(
      appDetails.appKey,
      selectedSiteProduct,
      user?.email,
      selectedAppPlan
    );

  const installedAppHrefResolver = (url: string) => {
    return getInstalledAppProductLink(url, user?.email);
  };

  const loginRedirectPath = UrlUtils.updateQueryParam(
    window.location.href,
    SITE_SELECTOR_VISIBLE_QUERY_PARAM,
    'true'
  );
  const appListingUser: AppListingUser = {
    isMpacAdmin: isAdmin,
    isLoggedIn,
    avatarUrl: userAvatarUrl,
    userId: user?.id,
  };

  const { showInstallationTab, showServerEolWarning, serverEolStartDate } =
    MarketplaceAppUtils.isServerAppEol(
      selectedHosting === AtlassianProductHostingType.Server,
      selectedAppVersion?.deployment?.compatibleProducts
    );
  const isServerAppEos = selectedHosting === AtlassianProductHostingType.Server;
  const isDataCenterAppEos = showDCEosMessaging()
    ? selectedHosting === AtlassianProductHostingType.DataCenter
    : false;
  return (
    <>
      <AppListingMeta
        appId={appDetails.appId}
        appName={appDetails.name}
        appSlug={appDetails.slug}
        appDescription={appDetails.tagline}
        canonicalLink={links.appListingCanonical}
        mediaImageLink={
          enableMediaFromProductListing() ? links.mediaImageFromProductListing : links.mediaImage
        }
        image={metaTagImage}
        logo={appDetails.logo}
      />
      {!showOneClickAppInstallDialog && !showTwoClickAppInstallFocusedTask && (
        <SiteSelectorDialogWrapper
          appId={appDetails.appId}
          installAppHrefResolver={installAppHrefResolver}
          requestAppHrefResolver={requestAppHrefResolver}
          appCompatibilities={cloudAppCompatibilities}
          appKey={appDetails.appKey}
          appName={appDetails.name}
          paymentModel={selectedAppVersion.paymentModel}
          appDetailsLink={links.appDetails}
          onClose={onSiteSelectorClose}
          isOpen={siteSelectorOpen}
          loginRedirectPath={loginRedirectPath}
          mpacBaseUrl={mpacBaseUrl}
          showAppPlans={showAppPlans}
          selectedPlanContext={selectedPlan}
          appPricingDetailsLink={appPricingDetailsLink}
          editionTypeList={editions?.map((edition) => edition.type) || []}
        />
      )}
      <PartnerPermissions.Provider value={permissions}>
        <AppListing
          appDetails={appDetails}
          availableSiteCloudIds={availableSiteCloudIds}
          editions={editions}
          reviewSummaryDetails={reviewSummaryDetails}
          appDetailsFromSearchResponse={appDetailsFromSearchResponse}
          components={{
            beforeHeader: renderBeforeHeader(),
            afterHeader: renderAfterHeader(),
          }}
          user={appListingUser}
          loginHref={loginHref}
          mpacBaseUrl={mpacBaseUrl}
          feedbackCollectorApiKeys={feedbackCollectorApiKeys}
          feedbackGatewayBaseUrl={feedbackGatewayBaseUrl}
          mpacCdnBaseUrl={mpacCdnBaseUrl}
          atlassianCommunityBaseUrl={atlassianCommunityBaseUrl}
          headerInjection={getHeaderInjection()}
          links={links}
          selectedHosting={selectedHosting}
          selectedTab={tab}
          cloudMigration={cloudMigrationInfo}
          onHostingChange={handleHostingChange}
          onTabSelect={handleTabChange}
          onSupportLinkClick={handleSupportLinkClick}
          onMediaViewerOpen={handleMediaViewerOpen}
          onWatchAppToggle={handleWatchAppToggle}
          onSeeMoreReviewsClick={handleSeeMoreReviewsClick}
          onArtifactDownloadClick={handleArtifactDownloadClick}
          onHostingSelectOpen={handleHostingSelectOpen}
          onHostingOptionClick={handleHostingOptionClick}
          onContactPartnerClick={handleVendorContactClick}
          onCloudMigrationResourceLinksClick={handleCloudMigrationResourcesLinksClick}
          onMigrationLozengeClick={handleMigrationLozengeClick}
          openMarketplaceLinksInNewTab={false}
          showHostingSelect
          showResourcesSection
          showInstallationTab={showInstallationTab}
          serverEolStartDate={serverEolStartDate}
          showVersionsTab
          showReviewsTab
          showInstallationCount
          showIntegrationsDetailsSection
          showPricingTab
          showHeaderProductCompatibilities
          showHeaderLozenges
          showCloudMigrationLozenge={true}
          onPricingCalculatorInstallCTAClick={onPricingCalculatorCTAClick}
          isServerAppEos={isServerAppEos}
          {...(showOneClickAppInstallDialog && {
            oneClickAppInstallDialog: {
              isOpen: siteSelectorOpen,
              onClose: onSiteSelectorClose,
              stargateBasePath: STARGATE_BASE_PATH,
              graphQLGatewayUrl,
              installAppHrefResolver,
              requestAppHrefResolver,
              onSiteSelectorFooterInstallCTAClick: handleSiteSelectorInstallCTAClick,
            },
          })}
          {...(!showOneClickAppInstallDialog &&
            showTwoClickAppInstallFocusedTask && {
              twoClickAppInstallFocusedTask: {
                handleFocusedTaskClose: onSiteSelectorClose,
                isFocusedTaskOpen: siteSelectorOpen,
                stargateBasePath: STARGATE_BASE_PATH,
                graphQLGatewayUrl,
                installAppHrefResolver,
                requestAppHrefResolver,
                installedAppHrefResolver,
                onSiteSelectorFooterInstallCTAClick: handleSiteSelectorInstallCTAClick,
                editionTypes: editions?.map((edition) => edition.type) || [],
                selectedPlan: selectedPlanForPlanSelector,
                setSelectedPlan: setSelectedPlanForPlanSelector,
                showAppPlans,
              },
            })}
        />
      </PartnerPermissions.Provider>
      {!__REACT_SSR__ &&
        props.appDetails.jsdWidgetKey &&
        !DOMUtils.isCrawler(window.navigator.userAgent) && (
          <AppListingJSDWidget
            jiraServiceDeskEmbedBaseUrl={jiraServiceDeskEmbedBaseUrl}
            jsdWidgetKey={props.appDetails.jsdWidgetKey}
          />
        )}
    </>
  );
};

const AppListingWithAnalyticsContext = (props: AppListingPageContentProps) => {
  const { appDetails, appId, selectedHosting, tab, selectedAppVersion } = props;
  const hosting = HostingTypeToQueryParam[selectedHosting];
  const { appKey } = appDetails || {};
  const paymentModel =
    selectedAppVersion.paymentModel === MarketplaceAppPaymentModel.Free ? 'free' : 'paid';
  const isEcosystemCloudApp =
    selectedAppVersion.deployment.__typename === AppDeployment.ECOSYSTEM_CLOUD;
  const { mpacBaseUrl } = BootstrapConfig.get();
  const cloudMigrationInfo = useCloudMigrationInfo(mpacBaseUrl, appKey);
  const isRunsOnAtlassianApp =
    appDetails?.tags?.marketingTags?.some((tag) => tag.name === RUNS_ON_ATLASSIAN_PROGRAM) || false;

  const analyticsContext = {
    appId,
    partnerId: appDetails?.partner?.id,
    appKey,
    appListingTabKey: tab,
    category: ANALYTICS_CATEGORY_APP_LISTING,
    hosting,
    isEcosystemCloudApp,
    paymentModel,
    alternateCloudAppKey: cloudMigrationInfo.alternateCloudAppKey,
    migrationAssistanceStatus: getMigrationAssistanceStatus(cloudMigrationInfo),
    migrationInformationAvailable: getMigrationInformationAvailable(cloudMigrationInfo),
    isRunsOnAtlassianApp,
  };

  return (
    <MarketplaceAnalyticsContext attributes={analyticsContext}>
      <AppListingPageContent {...props} cloudMigrationInfo={cloudMigrationInfo} />
    </MarketplaceAnalyticsContext>
  );
};

export default AppListingWithAnalyticsContext;
